import styled from '@emotion/styled';
import { useRef } from 'react';
import { colors, fonts, radii, breakpoints, ArrowRightIcon, ChevronDownIcon, CheckIcon, PanelText } from '../../../ui';
import { ExamArt } from './ExamArt';

// C6 "Examinations" picker (SPEC 4.3 #11, Figma session/04 right, 20 right, 27): image tiles per
// test category. A category with one test is picked by tapping the tile ("VISUAL FIELD ->");
// a category with several tests shows a native dropdown ("Please select a test and click 'next'").

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: center;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
  margin: auto 0;
  font-family: ${fonts.family};
  @media (max-width: ${breakpoints.phone - 1}px) {
    gap: 12px;
  }
`;

const Tile = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  /* four categories (e.g. + Colour Vision) still fit one row on an iPad landscape panel */
  width: ${({ dense }) => (dense ? 'clamp(140px, 21%, 236px)' : 'clamp(150px, 26%, 236px)')};
  background: #fff;
  border-radius: ${radii.dialog}px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12), 0 0 0 ${({ selected }) => (selected ? 2 : 1)}px ${({ selected }) => (selected ? colors.primary : colors.slate[100])};
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 120ms ease, transform 120ms ease;
  &:hover {
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.16), 0 0 0 ${({ selected }) => (selected ? 2 : 1)}px ${({ selected }) => (selected ? colors.primary : colors.slate[200])};
  }
  @media (max-width: 900px) {
    width: clamp(140px, 40%, 236px);
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    width: calc(50% - 6px);
  }
`;

const Art = styled.div`
  aspect-ratio: 4 / 3;
  overflow: hidden; /* keeps square artwork (Ishihara plate) inside the 4:3 box */
  padding: 10px 12px;
  box-sizing: border-box;
  background: #fff;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const Footer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 52px;
  padding: 8px 12px;
  box-sizing: border-box;
  border-top: 2px solid ${colors.tableHeader};
  font-size: 13px;
  line-height: 1.3;
  color: ${colors.slate[700]};
  .label {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
  .hint {
    color: ${colors.slate[600]};
  }
  .chosen {
    font-weight: 700;
    color: ${colors.primary};
  }
  svg {
    flex-shrink: 0;
    color: ${colors.tableHeader};
  }
  select {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    font-size: 16px; /* no zoom-on-focus on iOS */
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    font-size: 12px;
    min-height: 46px;
    padding: 6px 10px;
  }
`;

const Tick = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${colors.primary};
  color: #fff;
`;

const Message = styled.div`
  margin: auto;
  max-width: 420px;
  text-align: center;
`;

const CategoryTile = ({ category, selected, onSelect, dense }) => {
  const selectRef = useRef(null);
  const single = category.tests.length === 1;
  const chosen = selected && category.tests.find((t) => t.key === selected.key);

  const openPicker = () => {
    if (single) return onSelect(category.tests[0]);
    const el = selectRef.current;
    if (!el) return undefined;
    try {
      if (typeof el.showPicker === 'function') return el.showPicker();
    } catch (e) {
      /* showPicker needs a user gesture; fall back to focus */
    }
    return el.focus();
  };

  return (
    <Tile selected={Boolean(chosen)} dense={dense} data-testid={`exam-category-${category.key || category.id}`}>
      <Art onClick={openPicker}>{category.image ? <img src={category.image} alt='' /> : <ExamArt category={category} />}</Art>
      {chosen && (
        <Tick aria-hidden='true'>
          <CheckIcon size={14} strokeWidth={3} />
        </Tick>
      )}
      <Footer onClick={single ? openPicker : undefined}>
        {single ? (
          <>
            <span className={chosen ? 'chosen' : 'label'}>{chosen ? category.tests[0].label : category.title}</span>
            <ArrowRightIcon size={22} strokeWidth={2.5} />
          </>
        ) : (
          <>
            <span className={chosen ? 'chosen' : 'hint'}>{chosen ? chosen.label : "Please select a test and click 'next'"}</span>
            <ChevronDownIcon size={18} strokeWidth={2.5} />
            <select
              ref={selectRef}
              aria-label={`${category.title} tests`}
              value={chosen ? chosen.key : ''}
              onChange={(e) => onSelect(category.tests.find((t) => t.key === e.target.value))}
            >
              <option value='' disabled>
                {category.title}
              </option>
              {category.tests.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </>
        )}
      </Footer>
    </Tile>
  );
};

/**
 * @param categories  [{ id, key, title, image, tests: [{ key, test_id, label }] }] (examCatalog.toPickerCategories)
 * @param selected    the chosen test (or null)
 * @param onSelect    (test) => void
 * @param status      'loading' | 'ready' | 'error'
 */
export const ExamPickerView = ({ categories = [], selected, onSelect, status = 'ready' }) => {
  if (status === 'loading') {
    return (
      <Message>
        <PanelText>Loading examinations…</PanelText>
      </Message>
    );
  }
  if (status === 'error' || categories.length === 0) {
    return (
      <Message>
        <PanelText>
          {status === 'error'
            ? 'The examinations could not be loaded. Check the connection and open Examinations again.'
            : 'No examinations are enabled for this practice. An administrator can enable tests in the dashboard.'}
        </PanelText>
      </Message>
    );
  }
  return (
    <Grid role='list' aria-label='Examinations'>
      {categories.map((c) => (
        <CategoryTile key={c.id} category={c} selected={selected} onSelect={onSelect} dense={categories.length > 3} />
      ))}
    </Grid>
  );
};

export default ExamPickerView;
